import {
  Clapperboard,
  Compass,
  Crosshair,
  Eye,
  Heart,
  Laugh,
  Loader as LoadingIcon,
  Palette,
  Rocket,
  Shield,
  Skull,
  Sparkles,
  Users,
  Wand2,
  Zap,
} from 'lucide-react';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getRatingsOrWatchlist } from './api/movies';
import { Flex } from './components/Flex';
import { getLogEntryByUserId } from './helpers';
import { useAllUsers } from './hooks/useMovieData';
import { Movie } from './Movie';
import { useMovieModal } from './MovieModalContext';
import { NumberCounter } from './NumberCounter';
import { UserContext } from './UserContext';
import * as RecapCalc from './utils/recapCalculations';

export function Recap({}) {
  const { year, token } = useParams();
  const user_data = useContext(UserContext);
  const { openMovie } = useMovieModal();

  const [users, setUsers] = useState(null);
  const [userInfo, setUserInfo] = useState(null);

  const { users: allUsers, isLoading: usersLoading } = useAllUsers();

  useEffect(() => {
    if (!allUsers || usersLoading) {
      return;
    }

    async function loadUserData() {
      const usersWithData = [];

      const getInfo = async (user) => {
        const watchlist = await getRatingsOrWatchlist(user.username, false);
        const ratings = await getRatingsOrWatchlist(user.username, true);
        return { ...user, watchlist, ratings };
      };

      const results = await Promise.all(allUsers.map(getInfo));
      setUsers(results);
    }

    loadUserData();
  }, [allUsers, usersLoading]);

  useEffect(() => {
    if (!users) {
      return;
    }

    const yearNum = year ? parseInt(year, 10) : null;
    const ratings = getUserRatings(user_data.id, yearNum);
    const watchlist = getUserWatchlist(user_data.id, yearNum);

    ratings.sort((a, b) => RecapCalc.compareByUserRating(a, b, user_data.id));

    setUserInfo({
      ratings: ratings,
      watchlist: watchlist,
      watchedDuration: RecapCalc.getWatchedDuration(ratings),
      watchlistDuration: RecapCalc.getWatchedDuration(watchlist),
      top5: RecapCalc.getTopMovies(ratings, user_data.id, 5),
      bottom5: RecapCalc.getBottomMovies(ratings, user_data.id, 5),
      fromWatchlist: RecapCalc.getMoviesFromWatchlist(ratings, user_data.id),
      onRelease: RecapCalc.getMoviesOnRelease(ratings, user_data.id),
      genres: RecapCalc.getGenres(ratings),
      longestMovie: RecapCalc.getLongestMovie(ratings),
      oldestMovie: RecapCalc.getOldestMovie(ratings),
      averageRating: RecapCalc.getAverageRating(ratings, user_data.id),
      criticsWrong: RecapCalc.getCriticsWrong(ratings, user_data.id, 3),
      usersWrong: RecapCalc.getUsersWrong(ratings, user_data.id, 3),
    });
  }, [users, year, user_data.id]);

  function getUserRatings(user_id, year = null) {
    let data = users.filter((u) => u.id == user_id)[0].ratings;

    if (year) {
      data = data.filter((m) => {
        const entry = getLogEntryByUserId(m, user_id);
        if (entry?.rating_date) {
          const entryYear = new Date(entry?.rating_date).getFullYear();
          return entryYear == year;
        }
        return false;
      });
    }
    return data;
  }

  function getUserWatchlist(user_id, year = null) {
    let data = users.filter((u) => u.id == user_id)[0].watchlist;
    if (year) {
      data = data.filter((m) => {
        const entry = getLogEntryByUserId(m, user_id);
        if (entry?.watchlist_date) {
          const entryYear = new Date(entry?.watchlist_date).getFullYear();
          return entryYear == year;
        }
        return false;
      });
    }
    return data;
  }

  function GenreList({ genres }) {
    const genreConfig = {
      action: { icon: Zap, color: '#FF6B6B' },
      adventure: { icon: Compass, color: '#FFA500' },
      animation: { icon: Sparkles, color: '#9D4EDD' },
      comedy: { icon: Laugh, color: '#FFD700' },
      crime: { icon: Shield, color: '#2E2E2E' },
      documentary: { icon: Clapperboard, color: '#708090' },
      drama: { icon: Heart, color: '#E91E63' },
      family: { icon: Users, color: '#4CAF50' },
      fantasy: { icon: Wand2, color: '#6366F1' },
      history: { icon: Compass, color: '#8B4513' },
      horror: { icon: Skull, color: '#1a1a1a' },
      kids: { icon: Heart, color: '#FFB6C1' },
      music: { icon: Palette, color: '#FF1493' },
      mystery: { icon: Eye, color: '#4B0082' },
      romance: { icon: Heart, color: '#FF69B4' },
      'science-fiction': { icon: Rocket, color: '#00CED1' },
      'science fiction': { icon: Rocket, color: '#00CED1' },
      thriller: { icon: Eye, color: '#DC143C' },
      'tv-movie': { icon: Clapperboard, color: '#1E90FF' },
      'tv movie': { icon: Clapperboard, color: '#1E90FF' },
      war: { icon: Crosshair, color: '#A9A9A9' },
      western: { icon: Crosshair, color: '#8B4513' },
    };

    function getGenreConfig(genreName) {
      const normalized = genreName.toLowerCase();
      return (
        genreConfig[normalized] || { icon: Clapperboard, color: '#999999' }
      );
    }

    function GenreItem({ genre }) {
      const name = Object.keys(genre)[0];
      const value = Object.values(genre)[0];
      const config = getGenreConfig(name);
      const IconComponent = config.icon;

      return (
        <div className="fade-in">
          <Flex
            direction="row"
            align="center"
            justify="start"
            style={{ margin: '2rem', gap: '2rem' }}
          >
            <IconComponent
              size={100}
              strokeWidth={1.5}
              style={{ flexShrink: 0, color: config.color }}
            />

            <div>{name}</div>

            <NumberCounter endCount={value} />
          </Flex>
        </div>
      );
    }

    return (
      <Flex justify="center" className="section__list" direction="column">
        {genres.slice(0, 5).map((g, i) => (
          <GenreItem key={i} genre={g} />
        ))}
      </Flex>
    );
  }

  function RecapMovie({ movie }) {
    return <Movie movie={movie} style={{ height: '450px' }} />;
  }

  function RecapMovies({ movies }) {
    if (movies.length === 0) {
      return;
    }
    return (
      <Flex
        direction="row"
        style={{ flexWrap: 'wrap', gap: '8px', justifyContent: 'center' }}
      >
        {movies.map((m) => (
          <RecapMovie key={m.id} movie={m} />
        ))}
      </Flex>
    );
  }

  function Section({ children }) {
    const elementRef = useRef(null);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setIsVisible(true);
            }
          });
        },
        { threshold: 0.5 },
      );

      const element = elementRef.current;
      if (element) {
        observer.observe(element);
      }

      return () => {
        if (element) {
          observer.unobserve(element);
        }
      };
    }, []);

    const animations = ['grow-in', 'zoom-in'];
    const randomIndex = Math.floor(Math.random() * animations.length);
    const selectedAnimation = animations[randomIndex];

    return (
      <div
        ref={elementRef}
        className={`section ${isVisible ? selectedAnimation : ''}`.trim()}
      >
        {children}
      </div>
    );
  }

  if (parseInt(year) == new Date().getFullYear()) {
    return (
      <Flex align="center" direction="column">
        <div className="yearTitle">
          {year} is not over yet, {user_data.display_name ?? user_data.username}
          !
        </div>
      </Flex>
    );
  }

  if (userInfo == null) {
    return (
      <Flex justify="center" align="center" style={{ margin: '8px' }}>
        <LoadingIcon className="spin" /> Loading
      </Flex>
    );
  }

  return (
    userInfo?.ratings && (
      <Flex align="center" direction="column">
        <div className="yearTitle">
          {user_data.display_name ?? user_data.username}'s {year}
        </div>
        <div style={{ width: '100%' }}>
          <Section>
            <NumberCounter endCount={userInfo.ratings.length} /> movies watched
          </Section>
          <Section>
            Which sums to
            <NumberCounter
              formatAsTime={true}
              duration={1000}
              endCount={userInfo.watchedDuration}
            />
          </Section>
          <Section>
            The average rating was
            <NumberCounter duration={1000} endCount={userInfo.averageRating} />
          </Section>
          <Section>
            Longest movie you watched
            <RecapMovie movie={userInfo.longestMovie} />
          </Section>
          <Section>
            <NumberCounter endCount={userInfo.fromWatchlist.length} /> movies
            from watchlist
            <RecapMovies movies={userInfo.fromWatchlist} />
          </Section>
          <Section>
            Oldest movie
            <RecapMovie movie={userInfo.oldestMovie} />
          </Section>
          <Section>
            Favorite genres were
            <GenreList genres={userInfo.genres} />
          </Section>
          <Section>
            Top 5
            <RecapMovies movies={userInfo.top5} />
          </Section>
          <Section>
            Bottom 5
            <RecapMovies movies={userInfo.bottom5} />
          </Section>
          {userInfo.criticsWrong.length > 0 && (
            <Section>
              Critics wrong
              <RecapMovies movies={userInfo.criticsWrong} />
            </Section>
          )}
          {userInfo.usersWrong.length > 0 && (
            <Section>
              Users wrong
              <RecapMovies movies={userInfo.usersWrong} />
            </Section>
          )}
          {userInfo.onRelease.length > 0 && (
            <Section>
              {userInfo.onRelease.length} movie(s) watched on release
              <RecapMovies movies={userInfo.onRelease} />
            </Section>
          )}
          <Section>
            <NumberCounter endCount={userInfo.watchlist.length} /> movies added
            to watchlist
          </Section>
          <Section>
            Finishing {` ${year} `}watchlist will require
            <br />
            <NumberCounter
              formatAsTime={true}
              duration={1000}
              endCount={userInfo.watchlistDuration}
            />
          </Section>
        </div>
        <div></div>
      </Flex>
    )
  );
}
